import React from "react";
import { format } from "date-fns";
import { Link, useLocation } from "react-router-dom";

import { Span } from "../components/Typography/Typography";
import { LoadingSpinner } from "../components/LoadingSpinner/LoadingSpinner";
import { useProfile } from "../contexts/ProfileContext";
import {
    albumPath,
    Recommendation,
} from "../api/albums";
import { ClerkUser } from "../api/clerk";
import { ListenedAlbum } from "../components/ListenedAlbum/ListenedAlbum";
import { EmptyStateBox } from "../components/EmptyStateBox/EmptyStateBox";

import "./Profile.scss";

type GroupedRecommendation = {
    album_id: number;
    title: string;
    artist_name: string;
    source_cover_url: string | null;
    recommendations: Recommendation[];
};

function groupByAlbum(recommendations: Recommendation[]): GroupedRecommendation[] {
    const groups = new Map<number, GroupedRecommendation>();

    for (const rec of recommendations) {
        const existing = groups.get(rec.album_id);

        if (existing) {
            existing.recommendations.push(rec);
            continue;
        }

        groups.set(rec.album_id, {
            album_id: rec.album_id,
            title: rec.title,
            artist_name: rec.artist_name,
            source_cover_url: rec.source_cover_url,
            recommendations: [rec],
        });
    }

    return Array.from(groups.values());
}

function groupHistoryByDate(history: Recommendation[]) {
    const byDate = new Map<string, Recommendation[]>();

    for (const rec of history) {
        const dateKey = format(new Date(rec.updated_on), "yyyy-MM-dd");
        const existing = byDate.get(dateKey);

        if (existing) {
            existing.push(rec);
            continue;
        }

        byDate.set(dateKey, [rec]);
    }

    return Array.from(byDate.entries())
        .sort((a, b) => b[0].localeCompare(a[0]))
        .map(([dateKey, recommendations]) => ({
            dateKey,
            group: groupByAlbum(recommendations)[0],
        }))
        .filter((entry): entry is { dateKey: string; group: GroupedRecommendation } =>
            Boolean(entry.group)
        );
}

function formatRecommenderNames(
    recommendations: Recommendation[],
    usersByExternalId: Record<string, ClerkUser>
) {
    const names = recommendations
        .map((rec) => {
            const user = usersByExternalId[String(rec.from_user_id)];
            return user?.username ? `@${user.username}` : null;
        })
        .filter(Boolean);

    if (names.length === 0) {
        return "From friends";
    }

    if (names.length === 1) {
        return `From ${names[0]}`;
    }

    if (names.length === 2) {
        return `From ${names[0]} and ${names[1]}`;
    }

    return `From ${names.slice(0, -1).join(", ")}, and ${names[names.length - 1]}`;
}

function formatSentToFriendsCount(recommendations: Recommendation[]) {
    const friendCount = new Set(recommendations.map((rec) => rec.to_user_id)).size;

    if (friendCount === 1) {
        return "Sent to 1 friend";
    }

    return `Sent to ${friendCount} friends`;
}


function AlbumCover({ coverUrl, title }: { coverUrl: string | null; title: string }) {
    if (coverUrl) {
        return <img className="album-recommendations__cover" src={coverUrl} alt="" />;
    }

    return <div className="album-recommendations__cover album-recommendations__cover--placeholder" />;
}

function RecommendationsGrid({
    recommendations,
    isLoading,
    emptyMessage,
    groupSubtitleByAlbumId = {},
    defaultGroupSubtitle,
}: {
    recommendations: Recommendation[];
    isLoading: boolean;
    emptyMessage: string;
    groupSubtitleByAlbumId?: Record<number, string>;
    defaultGroupSubtitle?: (group: GroupedRecommendation) => string;
}) {
    if (isLoading) {
        return <LoadingSpinner />;
    }

    if (recommendations.length === 0) {
        return <EmptyStateBox>{emptyMessage}</EmptyStateBox>;
    }

    const items = groupByAlbum(recommendations);

    return (
        <ul className="album-recommendations__list">
            {items.map((group) => (
                <li key={group.album_id} className="album-recommendations__item">
                    <Link
                        className="album-recommendations__link"
                        to={albumPath(group.album_id)}
                    >
                        <AlbumCover coverUrl={group.source_cover_url} title={group.title} />
                        <div className="album-recommendations__meta">
                            <Span variant="body-3" weight="bold">
                                {group.title}
                            </Span>
                            <Span variant="body-4" color="mid-contrast">
                                {group.artist_name}
                            </Span>
                            <Span variant="body-4" color="mid-contrast">
                                {groupSubtitleByAlbumId[group.album_id] ??
                                    defaultGroupSubtitle?.(group)}
                            </Span>
                        </div>
                    </Link>
                </li>
            ))}
        </ul>
    );
}

function formatRecommenderLabel(
    recommendations: Recommendation[],
    usersByExternalId: Record<string, ClerkUser>
) {
    const text = formatRecommenderNames(recommendations, usersByExternalId);

    if (text === "From friends") {
        return "from friends";
    }

    return text.charAt(0).toLowerCase() + text.slice(1);
}

function formatListenedDateLabel(dateKey: string) {
    return format(new Date(dateKey), "MMM d, yyyy");
}

function ListenedHistoryList({
    history,
    usersByExternalId,
    isLoading,
    emptyMessage,
}: {
    history: { dateKey: string; group: GroupedRecommendation }[];
    usersByExternalId: Record<string, ClerkUser>;
    isLoading: boolean;
    emptyMessage: string;
}) {
    if (isLoading) {
        return <LoadingSpinner />;
    }

    if (history.length === 0) {
        return <EmptyStateBox>{emptyMessage}</EmptyStateBox>;
    }

    return (
        <div className="listened-albums-grid">
            {history.map(({ dateKey, group }) => (
                <ListenedAlbum
                    key={dateKey}
                    albumId={group.album_id}
                    title={group.title}
                    artistName={group.artist_name}
                    coverUrl={group.source_cover_url}
                    recommenderLabel={formatRecommenderLabel(
                        group.recommendations,
                        usersByExternalId
                    )}
                    listenedDateLabel={formatListenedDateLabel(dateKey)}
                />
            ))}
        </div>
    );
}

export function ProfileTab() {
    const location = useLocation();
    const {
        isCurrentUser,
        fullName,
        sent,
        sentSubtitleByAlbumId,
        isLoadingSharedTab,
        listenTabData,
        usersByExternalId,
        isLoadingListenTab,
    } = useProfile();

    const activeTab = location.pathname.endsWith("/listened") ? "listened" : "shared";

    const showListened = activeTab === "listened";
    const showShared = activeTab === "shared";

    const listenedHistory = groupHistoryByDate(listenTabData.history);

    return (
        <section className="profile-content" aria-label="Profile content">
            {showListened && (
                <div className="album-recommendations">
                    <ListenedHistoryList
                        history={listenedHistory}
                        usersByExternalId={usersByExternalId}
                        isLoading={isLoadingListenTab}
                        emptyMessage={
                            isCurrentUser
                                ? "Albums you listen to will show up here."
                                : `${fullName} hasn't listened to any albums yet.`
                        }
                    />
                </div>
            )}

            {showShared && (
                <div className="album-recommendations">
                    <RecommendationsGrid
                        recommendations={sent}
                        isLoading={isLoadingSharedTab}
                        emptyMessage={
                            isCurrentUser
                                ? "You haven't recommended any albums yet. Search for an album and share it with a friend."
                                : `${fullName} hasn't recommended any albums yet.`
                        }
                        groupSubtitleByAlbumId={sentSubtitleByAlbumId}
                        defaultGroupSubtitle={(group) =>
                            formatSentToFriendsCount(group.recommendations)
                        }
                    />
                </div>
            )}
        </section>
    );
}
